"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppSelector } from "@/store/store";
import Portal from "./Portal";

/**
 * PageLoader Component
 * A high-end, minimal full-page loader that appears on initial load/reload
 * or during logout.
 * Features a shimmering top progress bar and a subtle brand animation.
 */
export default function PageLoader() {
  const [loading, setLoading] = useState(true);
  const { isLoggingOut } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Artificial delay to allow for initial layout/asset loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2200);
    return () => clearTimeout(timer);
  }, []);

  const showLoader = loading || isLoggingOut;

  return (
    <AnimatePresence mode="wait">
      {showLoader && (
        <Portal>
          <motion.div
            key="page-loader"
            initial={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
            }}
            className="fixed inset-0 z-[99999] bg-background flex flex-col items-center justify-center overflow-hidden"
          >
            {/* Top Shimmer Loading Bar */}
            <div className="fixed top-0 left-0 right-0 h-[2px] bg-neutral-900 overflow-hidden">
              <motion.div
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2, ease: "easeInOut" }}
                className="h-full bg-accent-primary relative"
                style={{
                  boxShadow: "0 0 12px var(--color-accent-primary)",
                }}
              >
                {/* Shimmer Effect */}
                <motion.div
                  animate={{
                    x: ["-100%", "200%"],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="absolute inset-0 w-full h-full bg-linear-to-r from-transparent via-white/20 to-transparent"
                />
              </motion.div>
            </div>

            {/* Minimal Brand Element */}
            <div className="relative flex flex-col items-center gap-8">
              {/* Ambient Background Glow */}
              <motion.div
                animate={{
                  opacity: [0.05, 0.12, 0.05],
                  scale: [0.8, 1.1, 0.8],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 -m-20 blur-[100px] bg-accent-primary rounded-full"
              />

              {/* Geometric Logo Animation */}
              <div className="relative w-16 h-16">
                <svg
                  viewBox="0 0 72 72"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-full h-full"
                >
                  {/* Outer Frame */}
                  <motion.rect
                    x="14"
                    y="14"
                    width="44"
                    height="44"
                    rx="10"
                    stroke="var(--color-accent-primary)"
                    strokeWidth="1.5"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.2, ease: "easeInOut" }}
                  />

                  {/* Inner Dynamic Element */}
                  <motion.path
                    d="M36 26 L46 36 L36 46 L26 36 Z"
                    fill="var(--color-accent-primary)"
                    initial={{ scale: 0, opacity: 0, rotate: 45 }}
                    animate={{
                      scale: [0, 1, 0.9],
                      opacity: [0, 0.8, 0.4],
                      rotate: [45, 0, 45],
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />

                  {/* Connecting Lines */}
                  {[
                    { x1: 14, y1: 14, x2: 26, y2: 26 },
                    { x1: 58, y1: 58, x2: 46, y2: 46 },
                    { x1: 58, y1: 14, x2: 46, y2: 26 },
                    { x1: 14, y1: 58, x2: 26, y2: 46 },
                  ].map((line, i) => (
                    <motion.line
                      key={i}
                      {...line}
                      stroke="var(--color-accent-primary)"
                      strokeWidth="0.5"
                      strokeOpacity="0.3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8, duration: 0.5 }}
                    />
                  ))}
                </svg>
              </div>

              {/* Branding Text */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="flex flex-col items-center gap-2"
              >
                <span className="text-silver-gray text-[11px] font-medium uppercase tracking-[0.4em]">
                  Assetilo
                </span>
                <div className="h-[1px] w-8 bg-linear-to-r from-transparent via-accent-primary/40 to-transparent" />
              </motion.div>
            </div>

            {/* Footer Text */}
            <div className="absolute bottom-10 text-[9px] text-neutral-700 uppercase tracking-widest font-medium">
              Management System
            </div>
          </motion.div>
        </Portal>
      )}
    </AnimatePresence>
  );
}
